import { useState } from "react"
import { motion, AnimatePresence } from "motion/react"
import { ChevronUp } from "lucide-react"
import { cn } from "@/lib/utils"
import { useScrollSequence, ThinkingDots } from "./shared"

type ChatStep = "idle" | "userMessage" | "aiThinking" | "aiResponse" | "pills"

const USER_PROMPT =
    "Create a cinematic sunrise over Tokyo"
const AI_RESPONSE =
    "I'll create a 2-clip sequence: Tokyo skyline at golden hour and neon puddle reflections."

const PILLS = ["Generate all clips", "Add more detail"]

const STEPS: ChatStep[] = ["userMessage", "aiThinking", "aiResponse", "pills"]
const SEQUENCE = [
    { delay: 400 },
    { delay: 600 },
    { delay: 1200 },
    { delay: 800 },
]

export function TerminalBrowserPreviewBlock() {
    const [step, setStep] = useState<ChatStep>("idle")

    const { ref } = useScrollSequence(
        SEQUENCE,
        (i) => setStep(STEPS[i]),
        () => setStep("idle"),
        { amount: 0.6, margin: "40px 0px -40px 0px" },
    )

    const showUser = step !== "idle"
    const showThinking = step === "aiThinking"
    const showAI = step === "aiResponse" || step === "pills"
    const showPills = step === "pills"

    return (
        <div
            ref={ref}
            className="relative flex items-center justify-center px-6 md:px-12 pt-6 md:pt-12 pb-2 overflow-visible"
        >
            <div className="w-full max-w-lg bg-card rounded-xl border border-border h-[300px] flex flex-col overflow-hidden">
                <div className="p-4 md:p-6 space-y-4 flex-1 overflow-hidden">
                    <AnimatePresence>
                        {showUser && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.3 }}
                                className="flex flex-col gap-1 items-end"
                            >
                                <div className="max-w-[85%] inline-block rounded-2xl px-4 py-2.5 bg-neutral-700 text-white">
                                    <p className="text-sm leading-relaxed">{USER_PROMPT}</p>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <AnimatePresence>
                        {showThinking && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.25 }}
                                className="flex flex-col gap-1 items-start"
                            >
                                <div className="max-w-[85%] inline-block rounded-2xl px-4 py-2.5 bg-neutral-100">
                                    <ThinkingDots />
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <AnimatePresence>
                        {showAI && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.35 }}
                                className="flex flex-col gap-1 items-start"
                            >
                                <div className="max-w-[85%] inline-block rounded-2xl px-4 py-2.5 bg-neutral-100 text-neutral-700">
                                    <p className="text-sm leading-relaxed">{AI_RESPONSE}</p>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <AnimatePresence>
                        {showPills && (
                            <motion.div
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.3 }}
                                className="flex gap-2 flex-wrap"
                            >
                                {PILLS.map((pill, i) => (
                                    <motion.button
                                        key={pill}
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ delay: i * 0.1, duration: 0.2 }}
                                        className={cn(
                                            "rounded-full border border-border bg-card px-3.5 py-1.5",
                                            "text-xs font-medium text-foreground",
                                            "hover:bg-muted transition-colors"
                                        )}
                                    >
                                        {pill}
                                    </motion.button>
                                ))}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Input area */}
                <div className="px-4 pb-4 pt-2 mt-auto shrink-0">
                    <div className="flex gap-2 items-end">
                        <div className="flex-1 rounded-lg border border-neutral-300 px-3 py-2.5">
                            <span className="text-sm text-neutral-400">Describe your vision...</span>
                        </div>
                        <button className="shrink-0 w-10 h-10 rounded-lg bg-neutral-200 text-neutral-400 flex items-center justify-center">
                            <ChevronUp size={18} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}
